import { z } from "zod";
import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { assertPublicFormSecurity } from "@/lib/form-security.server";
import {
  sendContactSubmissionNotifications,
  sendQuoteSubmissionNotifications,
} from "@/lib/lead-notifications.server";

const OptionalAttribution = z.string().trim().max(1000).optional().or(z.literal(""));
const SecurityFields = {
  form_started_at: z.number().int().positive(),
  website_confirmation: z.string().max(200).optional().or(z.literal("")),
  turnstile_token: z.string().max(4000).optional().or(z.literal("")),
};
const QuoteSchema = z.object({
  full_name: z.string().trim().min(1).max(120), email: z.string().trim().email().max(255),
  company: z.string().trim().max(120).optional().or(z.literal("")), website: z.string().trim().max(255).optional().or(z.literal("")),
  phone: z.string().trim().max(40).optional().or(z.literal("")), shopify_plan: z.string().trim().max(60).optional().or(z.literal("")),
  current_revenue: z.string().trim().max(60).optional().or(z.literal("")), services: z.array(z.string().trim().max(60)).max(20).default([]),
  scope: z.string().trim().max(2000).optional().or(z.literal("")), timeline: z.string().trim().max(60).optional().or(z.literal("")),
  budget: z.string().trim().max(60).optional().or(z.literal("")), message: z.string().trim().max(4000).optional().or(z.literal("")),
  locale: z.enum(["en", "es"]).default("en"), engagement_type: z.enum(["fixed_price", "monthly_support"]).optional().or(z.literal("")),
  challenges: z.array(z.string().trim().max(80)).max(20).default([]), preferred_start_date: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal("")),
  monthly_revenue: z.string().trim().max(60).optional().or(z.literal("")), platform: z.string().trim().max(60).optional().or(z.literal("")),
  source_page: OptionalAttribution, landing_page: OptionalAttribution, landing_referrer: OptionalAttribution,
  first_seen_at: z.string().datetime().optional().or(z.literal("")), utm_source: OptionalAttribution,
  utm_medium: OptionalAttribution, utm_campaign: OptionalAttribution, utm_term: OptionalAttribution,
  utm_content: OptionalAttribution, gclid: OptionalAttribution, fbclid: OptionalAttribution,
  referral_code: z.string().trim().max(80).optional().or(z.literal("")),
  ...SecurityFields,
});
export type QuoteInput = z.infer<typeof QuoteSchema>;

function normalizeWebsite(value: string | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return `${url.protocol}//${url.hostname.toLowerCase()}${url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "")}`.slice(0, 255);
  } catch {
    return raw.slice(0, 255);
  }
}

async function resolveQuoteReferral(data: QuoteInput): Promise<{ partnerId: string | null; referralId: string | null }> {
  const code = data.referral_code?.trim();
  if (!code) return { partnerId: null, referralId: null };
  const partnerTable = (supabaseAdmin as any).from("referral_partners");
  const { data: partner, error: partnerError } = await partnerTable
    .select("id, attribution_days")
    .eq("status", "approved")
    .ilike("partner_code", code)
    .maybeSingle();
  if (partnerError) {
    console.error("resolveQuoteReferral partner error", partnerError);
    return { partnerId: null, referralId: null };
  }
  if (!partner) return { partnerId: null, referralId: null };

  const referralTable = (supabaseAdmin as any).from("partner_referrals");
  const now = new Date().toISOString();
  const { data: existing, error: existingError } = await referralTable
    .select("id")
    .eq("partner_id", partner.id)
    .ilike("referred_contact_email", data.email.toLowerCase())
    .in("status", ["submitted", "accepted", "contacted", "qualified", "scope_sent", "won"])
    .gt("attribution_expires_at", now)
    .limit(1);
  if (existingError) console.error("resolveQuoteReferral existing error", existingError);
  if (existing?.[0]?.id) return { partnerId: partner.id as string, referralId: existing[0].id as string };

  const attributionExpiresAt = new Date(Date.now() + Number(partner.attribution_days || 180) * 24 * 60 * 60 * 1000).toISOString();
  const { data: referral, error: referralError } = await referralTable.insert({
    partner_id: partner.id,
    referred_company: data.company || data.website || data.full_name,
    referred_contact_name: data.full_name,
    referred_contact_email: data.email.toLowerCase(),
    website: normalizeWebsite(data.website || undefined),
    introduction_context: "Prospect submitted the Shugert quote form through an approved partner referral link.",
    prospect_consented: true,
    status: "submitted",
    attribution_expires_at: attributionExpiresAt,
  }).select("id").single();
  if (referralError || !referral) {
    console.error("resolveQuoteReferral create error", referralError);
    return { partnerId: partner.id as string, referralId: null };
  }
  return { partnerId: partner.id as string, referralId: referral.id as string };
}

export const submitQuote = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => QuoteSchema.parse(input))
  .handler(async ({ data }) => {
    await assertPublicFormSecurity({
      form: "quote",
      input: data,
      minimumMs: 1500,
      maxRequests: 5,
      windowMs: 15 * 60 * 1000,
    });
    const sourceFromScope = data.scope?.match(/(?:Source|Origen):\s*([^\n]+)/i)?.[1]?.trim() ?? null;
    const table = supabaseAdmin.from("quotes") as any;
    const duplicateCutoff = new Date(Date.now() - 3 * 60 * 1000).toISOString();
    const { data: duplicate } = await table
      .select("id")
      .eq("email", data.email.toLowerCase())
      .gte("created_at", duplicateCutoff)
      .limit(1);
    if (duplicate?.length) {
      throw new Error("We already received a recent request from this email. Please wait before submitting again.");
    }

    const referral = await resolveQuoteReferral(data);
    const { data: row, error } = await table.insert({
      full_name: data.full_name, email: data.email.toLowerCase(), company: data.company || null,
      website: data.website || null, phone: data.phone || null, shopify_plan: data.shopify_plan || null,
      current_revenue: data.current_revenue || null, services: data.services, scope: data.scope || null,
      timeline: data.timeline || null, budget: data.budget || null, message: data.message || null,
      locale: data.locale, engagement_type: data.engagement_type || null, challenges: data.challenges,
      preferred_start_date: data.preferred_start_date || null, monthly_revenue: data.monthly_revenue || null,
      platform: data.platform || null, source_page: data.source_page || sourceFromScope,
      landing_page: data.landing_page || null, landing_referrer: data.landing_referrer || null,
      first_seen_at: data.first_seen_at || null, utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null, utm_campaign: data.utm_campaign || null,
      utm_term: data.utm_term || null, utm_content: data.utm_content || null,
      gclid: data.gclid || null, fbclid: data.fbclid || null,
      referral_code: data.referral_code || null,
      referral_partner_id: referral.partnerId,
      referral_id: referral.referralId,
    }).select("id").single();
    if (error || !row) { console.error("submitQuote error", error); throw new Error("Failed to submit quote. Please try again."); }

    if (referral.referralId) {
      const { error: linkError } = await (supabaseAdmin as any)
        .from("partner_referrals")
        .update({ quote_id: row.id })
        .eq("id", referral.referralId);
      if (linkError) console.error("submitQuote referral link error", linkError);
    }

    const notifications = await sendQuoteSubmissionNotifications(data, row.id as string);
    if (notifications.internal === "failed") {
      console.error(`[quote:${row.id}] internal notification failed; quote remains available in Admin Opportunities`);
    }
    if (notifications.prospect === "failed") {
      console.error(`[quote:${row.id}] prospect confirmation failed for ${data.email}`);
    }
    return { id: row.id as string, notifications };
  });

const ContactSchema = z.object({
  full_name: z.string().trim().min(1).max(120), email: z.string().trim().email().max(255),
  company: z.string().trim().max(120).optional().or(z.literal("")), message: z.string().trim().min(1).max(4000),
  locale: z.enum(["en", "es"]).default("en"),
  attribution_context: z.string().trim().max(4000).optional().or(z.literal("")),
  ...SecurityFields,
});
export const submitContact = createServerFn({ method: "POST" }).inputValidator((input: unknown) => ContactSchema.parse(input)).handler(async ({ data }) => {
  await assertPublicFormSecurity({
    form: "contact",
    input: data,
    minimumMs: 1200,
    maxRequests: 5,
    windowMs: 15 * 60 * 1000,
  });
  const table = supabaseAdmin.from("contact_submissions") as any;
  const duplicateCutoff = new Date(Date.now() - 3 * 60 * 1000).toISOString();
  const { data: duplicate } = await table
    .select("id")
    .eq("email", data.email.toLowerCase())
    .gte("created_at", duplicateCutoff)
    .limit(1);
  if (duplicate?.length) {
    throw new Error("We already received a recent message from this email. Please wait before submitting again.");
  }
  const attribution = data.attribution_context
    ? `\n\n--- Marketing attribution ---\n${data.attribution_context}`
    : "";
  const { error } = await table.insert({
    full_name: data.full_name,
    email: data.email.toLowerCase(),
    company: data.company || null,
    message: `${data.message}${attribution}`.slice(0, 8000),
    locale: data.locale,
  });
  if (error) { console.error("submitContact error", error); throw new Error("Failed to send message. Please try again."); }

  const notifications = await sendContactSubmissionNotifications(data);
  if (notifications.internal === "failed") console.error(`[contact:${data.email}] internal notification failed`);
  if (notifications.prospect === "failed") console.error(`[contact:${data.email}] prospect confirmation failed`);
  return { ok: true, notifications };
});

const QuoteIdSchema = z.object({ id: z.string().uuid() });
export const getQuoteById = createServerFn({ method: "GET" }).inputValidator((input: unknown) => QuoteIdSchema.parse(input)).handler(async ({ data }) => {
  const { data: row, error } = await supabaseAdmin.from("quotes").select("id, services, timeline, locale, created_at").eq("id", data.id).maybeSingle();
  if (error) { console.error("getQuoteById error", error); throw new Error("Could not load quote"); }
  return { quote: row };
});
